import React, { PureComponent } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, RadioGroup, Radio, Select } from '@deephaven/components';
import { vsLink, dhUnlink, dhTable, dhNewCircleLargeFilled, vsCircleLargeFilled, vsTrash } from '@deephaven/icons';
import Log from '@deephaven/log';
import { bindAllMethods } from '@deephaven/utils';
import { nanoid } from 'nanoid';
import { BarIcon, HistogramIcon, LineIcon, PieIcon, ScatterIcon } from "./icons/index.js";
import "./ChartBuilder.css";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('ChartBuilder');
/**
 * Form for configuring all the settings when creating a console.
 */
class ChartBuilder extends PureComponent {
  static getMaxSeriesCount(dh, type) {
    switch (type) {
      case dh.plot.SeriesPlotStyle.PIE:
        return 1;
      case dh.plot.SeriesPlotStyle.HISTOGRAM:
        return 0;
      default:
        return 100;
    }
  }
  static makeSeriesItem(value) {
    return {
      id: nanoid(),
      value
    };
  }
  static makeDefaultSeriesItems(dh, type, columns) {
    var maxSeriesCount = ChartBuilder.getMaxSeriesCount(dh, type);
    if (maxSeriesCount === 0 || columns == null || columns.length === 0) {
      return [];
    }
    var value = columns.length > 1 ? columns[1].name : columns[0].name;
    return [ChartBuilder.makeSeriesItem(value)];
  }
  static getDefaultXAxis(type, columns) {
    if (columns != null && columns.length > 0) {
      return columns[0].name;
    }
    return null;
  }
  constructor(props) {
    super(props);
    bindAllMethods(this);
    var model = props.model;
    var columns = model.columns,
      dh = model.dh;
    var type = this.getTypes()[0];
    var xAxis = ChartBuilder.getDefaultXAxis(type, columns);
    var seriesItems = ChartBuilder.makeDefaultSeriesItems(dh, type, columns);
    this.state = {
      /** The selected chart type */
      type,
      /** Array of column names of the series to display */
      seriesItems,
      /** The column name to use as the x-axis */
      xAxis,
      /** Whether the newly created chart should be linked with the table (update when filters update) */
      isLinked: true
    };
  }
  getTypes() {
    var model = this.props.model;
    var dh = model.dh;
    return [dh.plot.SeriesPlotStyle.LINE, dh.plot.SeriesPlotStyle.BAR, dh.plot.SeriesPlotStyle.SCATTER, dh.plot.SeriesPlotStyle.PIE
    // IDS-6808: Disable Histogram in Chart Builder until we pipe histogram creation through the API
    // dh.plot.SeriesPlotStyle.HISTOGRAM,
    ];
  }

  /**
   * Converts the provided chart type into a readable type.
   * Just replaces underscores with spaces and capitals the first letter of each word.
   */
  getTypeName(type) {
    var model = this.props.model;
    var dh = model.dh;
    switch (type) {
      case dh.plot.SeriesPlotStyle.LINE:
        return 'Line';
      case dh.plot.SeriesPlotStyle.BAR:
        return 'Bar';
      case dh.plot.SeriesPlotStyle.SCATTER:
        return 'Scatter';
      case dh.plot.SeriesPlotStyle.PIE:
        return 'Pie';
      case dh.plot.SeriesPlotStyle.HISTOGRAM:
        return 'Histogram';
      default:
        return type;
    }
  }
  getTypeIcon(type) {
    var model = this.props.model;
    var dh = model.dh;
    switch (type) {
      case dh.plot.SeriesPlotStyle.LINE:
        return /*#__PURE__*/_jsx(LineIcon, {});
      case dh.plot.SeriesPlotStyle.BAR:
        return /*#__PURE__*/_jsx(BarIcon, {});
      case dh.plot.SeriesPlotStyle.SCATTER:
        return /*#__PURE__*/_jsx(ScatterIcon, {});
      case dh.plot.SeriesPlotStyle.PIE:
        return /*#__PURE__*/_jsx(PieIcon, {});
      case dh.plot.SeriesPlotStyle.HISTOGRAM:
        return /*#__PURE__*/_jsx(HistogramIcon, {});
      default:
        return null;
    }
  }
  getXAxisLabel(type) {
    var model = this.props.model;
    var dh = model.dh;
    switch (type) {
      case dh.plot.SeriesPlotStyle.PIE:
        return 'Labels';
      case dh.plot.SeriesPlotStyle.HISTOGRAM:
        return 'Data';
      default:
        return 'X-Axis';
    }
  }
  getSeriesLabel(type) {
    var model = this.props.model;
    var dh = model.dh;
    switch (type) {
      case dh.plot.SeriesPlotStyle.PIE:
        return 'Values';
      default:
        return 'Series';
    }
  }
  handleAddSeries() {
    this.setState(state => {
      var seriesItems = state.seriesItems;
      var newSeriesItems = [...seriesItems];
      var model = this.props.model;
      var columns = model.columns;
      newSeriesItems.push({
        id: nanoid(),
        value: columns[0].name
      });
      return {
        seriesItems: newSeriesItems
      };
    }, this.sendChange);
  }
  handleLinkStateChange(value) {
    this.setState({
      isLinked: value === 'true'
    }, this.sendChange);
  }
  handleReset() {
    var model = this.props.model;
    var columns = model.columns,
      dh = model.dh;
    var type = this.getTypes()[0];
    var xAxis = ChartBuilder.getDefaultXAxis(type, columns);
    var seriesItems = ChartBuilder.makeDefaultSeriesItems(dh, type, columns);
    var isLinked = true;
    this.setState({
      type,
      seriesItems,
      xAxis,
      isLinked
    }, this.sendChange);
  }
  handleSeriesChange(eventTargetValue, index) {
    var value = eventTargetValue;
    this.setState(state => {
      var seriesItems = state.seriesItems;
      seriesItems = [...seriesItems];
      seriesItems[index].value = value;
      return {
        seriesItems
      };
    }, this.sendChange);
  }
  handleSeriesDeleteClick(index) {
    this.setState(state => {
      var seriesItems = state.seriesItems;
      var newSeriesItems = [...seriesItems];
      newSeriesItems.splice(index, 1);
      return {
        seriesItems: newSeriesItems
      };
    }, this.sendChange);
  }
  handleSubmit(event) {
    event.preventDefault();
    var onSubmit = this.props.onSubmit;
    var _this$state = this.state,
      type = _this$state.type,
      seriesItems = _this$state.seriesItems,
      xAxis = _this$state.xAxis,
      isLinked = _this$state.isLinked;
    var series = seriesItems.map(item => item.value);
    onSubmit({
      type,
      series,
      xAxis,
      isLinked
    });
  }
  handleTypeClick(index) {
    var type = this.getTypes()[index];
    log.debug2('handleTypeSelect', type);
    this.setState(state => {
      var model = this.props.model;
      var dh = model.dh;
      var maxSeriesCount = ChartBuilder.getMaxSeriesCount(dh, type);
      var seriesItems = state.seriesItems;
      seriesItems = seriesItems.slice(0, maxSeriesCount);
      if (seriesItems.length === 0 && maxSeriesCount > 0) {
        var columns = model.columns;
        seriesItems = ChartBuilder.makeDefaultSeriesItems(dh, type, columns);
      }
      return {
        type,
        seriesItems
      };
    }, this.sendChange);
  }
  handleXAxisChange(eventTargetValue) {
    var xAxis = eventTargetValue;
    log.debug2('x-axis change', xAxis);
    this.setState({
      xAxis
    }, this.sendChange);
  }
  sendChange() {
    var onChange = this.props.onChange;
    var _this$state2 = this.state,
      isLinked = _this$state2.isLinked,
      type = _this$state2.type,
      seriesItems = _this$state2.seriesItems,
      xAxis = _this$state2.xAxis;
    var series = seriesItems.map(item => item.value);
    onChange({
      type,
      series,
      xAxis,
      isLinked
    });
  }
  render() {
    var model = this.props.model;
    var columns = model.columns,
      dh = model.dh;
    var _this$state3 = this.state,
      seriesItems = _this$state3.seriesItems,
      type = _this$state3.type,
      xAxis = _this$state3.xAxis,
      isLinked = _this$state3.isLinked;
    var maxSeriesCount = ChartBuilder.getMaxSeriesCount(dh, type);
    var xAxisLabel = this.getXAxisLabel(type);
    var seriesLabel = this.getSeriesLabel(type);
    var isSeriesVisible = seriesItems.length > 0;
    var isAddSeriesVisible = seriesItems.length < maxSeriesCount;
    return /*#__PURE__*/_jsx("div", {
      className: "chart-builder",
      children: /*#__PURE__*/_jsxs("form", {
        onSubmit: this.handleSubmit,
        children: [/*#__PURE__*/_jsxs("div", {
          className: "form-row",
          children: [/*#__PURE__*/_jsx("label", {
            children: "Select Chart Type"
          }), /*#__PURE__*/_jsx("div", {
            className: "form-row",
            children: this.getTypes().map((chartType, index) => {
              var key = chartType;
              return /*#__PURE__*/_jsx("div", {
                className: "col col-chart-type",
                children: /*#__PURE__*/_jsxs("button", {
                  type: "button",
                  className: classNames('btn', 'btn-icon', 'btn-chart-type', {
                    active: chartType === type
                  }),
                  onClick: () => this.handleTypeClick(index),
                  children: [this.getTypeIcon(chartType), this.getTypeName(chartType)]
                })
              }, key);
            })
          })]
        }), /*#__PURE__*/_jsx("hr", {}), /*#__PURE__*/_jsxs("div", {
          className: "form-row form-inline",
          children: [/*#__PURE__*/_jsx("label", {
            className: "col-2 label-left",
            children: xAxisLabel
          }), /*#__PURE__*/_jsx(Select, {
            className: "form-control select-x-axis col",
            value: xAxis,
            onChange: this.handleXAxisChange,
            children: columns.map(column => /*#__PURE__*/_jsx("option", {
              value: column.name,
              children: column.name
            }, column.name))
          })]
        }), isSeriesVisible && /*#__PURE__*/_jsx("hr", {}), seriesItems.map((seriesItem, i) => /*#__PURE__*/_jsxs("div", {
          className: "form-row form-inline form-series-item",
          "data-testid": "form-series-item-".concat(i),
          children: [/*#__PURE__*/_jsx("label", {
            className: "col-2 label-left",
            children: i === 0 ? seriesLabel : ''
          }), /*#__PURE__*/_jsx(Select, {
            className: "form-control select-series col",
            value: seriesItem.value,
            onChange: v => this.handleSeriesChange(v, i),
            "data-testid": "select-series-item-".concat(i),
            children: columns.map(column => /*#__PURE__*/_jsx("option", {
              value: column.name,
              children: column.name
            }, column.name))
          }), seriesItems.length > 1 && /*#__PURE__*/_jsx(Button, {
            kind: "ghost",
            className: "btn-delete-series ml-2 px-2",
            "data-testid": "delete-series-".concat(i),
            onClick: () => {
              this.handleSeriesDeleteClick(i);
            },
            icon: vsTrash,
            tooltip: "Delete"
          })]
        }, seriesItem.id)), isAddSeriesVisible && /*#__PURE__*/_jsxs("div", {
          className: "form-row",
          children: [/*#__PURE__*/_jsx("div", {
            className: "col-2"
          }), /*#__PURE__*/_jsx(Button, {
            kind: "ghost",
            className: "btn-add-series mt-1",
            onClick: this.handleAddSeries,
            icon: dhNewCircleLargeFilled,
            children: "Add Series"
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "form-row chart-builder-link",
          children: [/*#__PURE__*/_jsx("label", {
            className: "col-2 label-right",
            children: /*#__PURE__*/_jsxs("div", {
              className: "fa-md fa-layers",
              children: [/*#__PURE__*/_jsx(FontAwesomeIcon, {
                mask: dhTable,
                icon: vsCircleLargeFilled,
                transform: "right-5 down-5"
              }), /*#__PURE__*/_jsx(FontAwesomeIcon, {
                icon: isLinked ? vsLink : dhUnlink,
                transform: "grow-2 right-8 down-6"
              })]
            })
          }), /*#__PURE__*/_jsxs(RadioGroup, {
            "aria-label": "Link state options",
            orientation: "horizontal",
            onChange: this.handleLinkStateChange,
            value: "".concat(isLinked),
            children: [/*#__PURE__*/_jsx(Radio, {
              value: "true",
              children: "Sync State"
            }), /*#__PURE__*/_jsx(Radio, {
              value: "false",
              children: "Freeze State"
            })]
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "form-row",
          children: [/*#__PURE__*/_jsx("div", {
            className: "col-2 label-right"
          }), /*#__PURE__*/_jsx("div", {
            className: "col chart-builder-link-info",
            children: isLinked ? 'Charts with synced state will update to match any filters or user modifications applied to the parent table.' : 'Freeze State disconnects the chart state from the parent table. New filters or user modifications on the parent table will not be applied.'
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: classNames('form-row', 'justify-content-end', 'my-3'),
          children: [/*#__PURE__*/_jsx(Button, {
            kind: "secondary",
            className: "btn-reset",
            onClick: this.handleReset,
            children: "Reset"
          }), /*#__PURE__*/_jsx(Button, {
            kind: "primary",
            type: "submit",
            className: "btn-submit",
            children: "Create"
          })]
        })]
      })
    });
  }
}
export default ChartBuilder;
//# sourceMappingURL=ChartBuilder.js.map